import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  LayoutDashboard, Users, PhoneCall, PhoneOutgoing, PhoneIncoming, Megaphone, AudioLines,
  Workflow, BarChart3, FileBarChart, Settings as SettingsIcon, Menu, X, LogOut, ArrowUpLeft, CalendarCheck, ShieldCheck, Hash,
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import Avatar from "@/components/Avatar";
import { useAuth } from "@/lib/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/leads", label: "CRM Leads", icon: Users },
  { to: "/dashboard/appointments", label: "Appointments", icon: CalendarCheck },
  { to: "/dashboard/voice-agents", label: "AI Voice Agents", icon: PhoneCall },
  { to: "/dashboard/talk", label: "Talk to AI", icon: AudioLines },
  { to: "/dashboard/calling", label: "Automatic Calling", icon: PhoneOutgoing },
  { to: "/dashboard/incoming-calls", label: "Incoming Calls", icon: PhoneIncoming },
  { to: "/dashboard/phone-numbers", label: "Phone Numbers", icon: Hash },
  { to: "/dashboard/marketing", label: "AI Marketing Studio", icon: Megaphone },
  { to: "/dashboard/automations", label: "Automations", icon: Workflow },
  { to: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/dashboard/reports", label: "Reports", icon: FileBarChart },
  { to: "/dashboard/settings", label: "Settings", icon: SettingsIcon },
];

const OWNER_NAV = { to: "/dashboard/owner-console", label: "Owner Console", icon: ShieldCheck };

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col">
      <Link to="/" data-testid="dashboard-brand" className="flex items-center gap-2.5 px-5 py-5">
        <BrandLogo />
        <span className="text-base font-bold tracking-tight text-[#111111]">Savy Kerno</span>
      </Link>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2" data-testid="dashboard-nav">
        {[...NAV, ...(user?.role === "admin" ? [OWNER_NAV] : [])].map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={`side-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-[#111111] text-white shadow-sm"
                    : "text-neutral-600 hover:translate-x-0.5 hover:bg-neutral-100 hover:text-[#111111]"
                }`
              }
            >
              <Icon size={17} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-neutral-100 p-4">
        <Link to="/" className="mb-2 flex items-center gap-2.5 rounded-xl px-3 py-2 text-xs font-medium text-neutral-500 hover:bg-neutral-100">
          <ArrowUpLeft size={14} /> Back to website
        </Link>
        <div className="flex items-center gap-3 px-3 py-2">
          <Avatar user={user} className="h-8 w-8 text-xs" testid="sidebar-avatar" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[#111111]">{user?.name}</p>
            <p className="truncate text-[11px] text-neutral-400">{user?.email}</p>
          </div>
          <button
            data-testid="sign-out-button"
            aria-label="Sign out"
            onClick={() => {
              logout();
              navigate("/");
            }}
            className="text-neutral-400 hover:text-[#111111]"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const reduce = useReducedMotion();

  return (
    <div className="min-h-screen bg-neutral-50/60 text-[#111111]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-neutral-200 bg-white lg:block print:hidden">
        <SidebarContent />
      </aside>

      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-neutral-200 bg-white/90 px-4 py-3 backdrop-blur lg:hidden print:hidden">
        <Link to="/dashboard" className="flex items-center gap-2">
          <BrandLogo className="h-7 w-7" />
          <span className="text-sm font-bold">Savy Kerno</span>
        </Link>
        <button
          data-testid="dashboard-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setMenuOpen((v) => !v)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200"
        >
          {menuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMenuOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white shadow-2xl">
            <SidebarContent onNavigate={() => setMenuOpen(false)} />
          </aside>
        </div>
      )}

      <main className="lg:pl-64 print:pl-0">
        <motion.div
          key={location.pathname}
          initial={{ opacity: reduce ? 1 : 0, y: reduce ? 0 : 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-5xl px-4 py-6 sm:px-8 sm:py-10"
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}
